/** Markup de react-mentions: @[display](id) */
export const MENTION_MARKUP_REGEX = /@\[(.*?)\]\(([^)]+)\)/g;

/** Mención guardada o normalizada: @usuario@dominio.tld (espacio opcional tras @).
 * El local part admite '+' (correos con sufijo, p. ej. nombre+1@dominio.cl). */
export const MENTION_EMAIL_REGEX = /(@\s*[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z0-9.-]+)/g;

export const SHARED_LINK_LABEL = "[link compartido]";

/** URLs http(s) o dominios www. */
export const COMMENT_URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)}\]'"]+$/;

/**
 * Convierte markup de menciones a texto plano @email antes de persistir o mostrar.
 */
export function cleanMentionMarkupForStorage(text = "") {
  if (!text) return "";
  return String(text).replace(MENTION_MARKUP_REGEX, "@$1");
}

/** Alias para lectura en UI */
export const normalizeCommentTextForDisplay = cleanMentionMarkupForStorage;

export function stripLinkTrailingPunctuation(raw = "") {
  const value = String(raw);
  const stripped = value.replace(TRAILING_PUNCTUATION_REGEX, "");
  return stripped.length > 0 ? stripped : value;
}

export function normalizeCommentLinkHref(raw = "") {
  const href = stripLinkTrailingPunctuation(String(raw).trim());
  if (/^www\./i.test(href)) return `https://${href}`;
  return href;
}

function splitTextSegmentIntoParts(text = "", includeLinks = true) {
  if (!text) return [];

  if (!includeLinks) {
    return [{ type: "text", value: text }];
  }

  const parts = [];
  const regex = new RegExp(COMMENT_URL_REGEX.source, "g");
  let lastIndex = 0;
  let match = regex.exec(text);

  while (match) {
    const rawUrl = match[0];
    const stripped = stripLinkTrailingPunctuation(rawUrl);
    const href = normalizeCommentLinkHref(stripped);
    const trailing = rawUrl.slice(stripped.length);

    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }

    if (href) {
      parts.push({
        type: "link",
        value: href,
        href,
        label: SHARED_LINK_LABEL,
      });
    }

    if (trailing) {
      parts.push({ type: "text", value: trailing });
    }

    lastIndex = match.index + rawUrl.length;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }

  if (parts.length === 0) {
    parts.push({ type: "text", value: text });
  }

  return parts;
}

/**
 * Partes de texto plano (texto | link).
 * @returns {{ type: 'text' | 'link', value: string, href?: string, label?: string }[]}
 */
export function parseLinksInPlainText(text = "") {
  if (!text) return [];
  return splitTextSegmentIntoParts(text, true);
}

/**
 * Partes de un comentario para render (texto | mención | link).
 * @returns {{ type: 'text' | 'mention' | 'link', value: string, href?: string, label?: string }[]}
 */
export function parseCommentDisplayParts(text = "") {
  const normalized = normalizeCommentTextForDisplay(text);
  if (!normalized) return [];

  const parts = [];
  const regex = new RegExp(MENTION_EMAIL_REGEX.source, "g");
  let lastIndex = 0;
  let match = regex.exec(normalized);

  while (match) {
    if (match.index > lastIndex) {
      parts.push(...splitTextSegmentIntoParts(normalized.slice(lastIndex, match.index), true));
    }
    parts.push({ type: "mention", value: match[0] });
    lastIndex = match.index + match[0].length;
    match = regex.exec(normalized);
  }

  if (lastIndex < normalized.length) {
    parts.push(...splitTextSegmentIntoParts(normalized.slice(lastIndex), true));
  }

  if (parts.length === 0) {
    parts.push({ type: "text", value: normalized });
  }

  return parts;
}

/**
 * Partes de un comentario para render (texto | mención).
 * @returns {{ type: 'text' | 'mention', value: string }[]}
 */
export function parseCommentMentionParts(text = "") {
  return parseCommentDisplayParts(text).map((part) =>
    part.type === "link"
      ? { type: "text", value: part.label || SHARED_LINK_LABEL }
      : part
  );
}

export function getDefaultMentionPillStyle(teal = "#5AADA5") {
  return {
    backgroundColor: teal,
    color: "#ffffff",
    padding: "2px 8px",
    borderRadius: "16px",
    fontWeight: "500",
    display: "inline-block",
    fontSize: "12px",
    margin: "0 4px",
  };
}
